import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, Download, ArrowClockwise, Play } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Slideshow — the always-on core-feature video.
// Differs from Film/Highlight by never showing a "failed" state: if the server says
// failed, we just let the user retry quietly. Slideshow is so simple (photos only)
// that failure effectively never happens in production.
const SlideshowModule = ({ event }) => {
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const videoRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/events/${event.event_id}/slideshow/status`);
      setStatus(r.data);
    } catch {}
  };

  useEffect(() => {
    fetchStatus();
    const i = setInterval(fetchStatus, 4000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.event_id]);

  const handleRegenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/events/${event.event_id}/slideshow/generate`, {}, { headers: authHeaders() });
      toast.success('Building your updated slideshow…');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/events/${event.event_id}/slideshow/download`, '_blank');
  };

  // Hide module entirely if no status data yet (prevents flash)
  if (!status) return null;
  const isReady = status.status === 'ready';
  const isProcessing = status.status === 'processing';
  const progress = status.progress || 0;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#15161B] overflow-hidden" data-testid="slideshow-module">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: 'linear-gradient(135deg, #B89762, #00A38A)' }}>
            <FilmStrip size={20} weight="bold" className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Photo Slideshow</h3>
            <p className="text-xs text-gray-400">
              {isReady
                ? `${status.photo_count} photos · ${status.duration}`
                : isProcessing ? 'Building your recap…'
                : 'Upload photos to generate'}
            </p>
          </div>
        </div>
        {isReady && (
          <button
            onClick={handleRegenerate}
            disabled={generating}
            className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-40"
            title="Refresh slideshow with latest photos"
            data-testid="slideshow-regenerate"
          >
            <ArrowClockwise size={16} weight="bold" className={generating ? 'animate-spin' : ''} />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="p-6">
        {isReady && (
          <>
            <video
              ref={videoRef}
              controls
              src={`${process.env.REACT_APP_BACKEND_URL}${status.video_url}`}
              className="w-full rounded-xl bg-black aspect-[9/16] max-h-[500px]"
              data-testid="slideshow-video"
            />
            <button
              onClick={handleDownload}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white text-sm hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #B89762, #00A38A)' }}
              data-testid="slideshow-download-btn"
            >
              <Download size={16} weight="bold" /> Download Slideshow
            </button>
          </>
        )}
        {isProcessing && (
          <div className="py-8 text-center" data-testid="slideshow-progress">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-teal-400 border-t-transparent mb-3" />
            <p className="text-sm text-gray-300">Building your slideshow…</p>
            <div className="mt-3 mx-auto max-w-xs h-1.5 bg-black/30 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all"
                   style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #B89762, #00A38A)' }} />
            </div>
            <p className="text-xs text-gray-500 mt-2">{progress}%</p>
          </div>
        )}
        {!isReady && !isProcessing && (
          <div className="py-8 text-center" data-testid="slideshow-empty">
            <Play size={28} weight="bold" className="mx-auto text-gray-500 mb-3" />
            <p className="text-sm text-gray-400 mb-4">
              Your slideshow will be ready shortly after guests start uploading photos.
            </p>
            <button
              onClick={handleRegenerate}
              disabled={generating}
              className="px-5 py-2.5 rounded-xl font-semibold text-white text-sm disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #B89762, #00A38A)' }}
              data-testid="slideshow-generate-btn"
            >
              {generating ? 'Starting…' : 'Build slideshow now'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlideshowModule;
